import {DARK_MAP_STYLE} from './google-map-styles.js';
import {fleetPoints} from './fleet-map.js';
import {deviceName} from './device-profile.js';
import {ZONE_STYLE} from './zone-style.js';
import {MAP_MIN_ZOOM,WEB_MAP_MAX_ZOOM,nextZoom,zoomAvailability} from './map-zoom.js';

const valid=p=>Number.isFinite(p?.latitude)&&Number.isFinite(p?.longitude)&&Math.abs(p.latitude)<=90&&Math.abs(p.longitude)<=180;
const coordinate=p=>({lat:p.latitude,lng:p.longitude});

// One controller owns one Map. Live fixes and playback update its overlays;
// they never call the Map constructor or Google Routes/Geocoding services.
/** @param {any} maps @param {any} element @param {(state:{in:boolean,out:boolean})=>void} onZoomChange */
export function createGoogleMapController(maps,element,onZoomChange=()=>{}){
 const map=new maps.Map(element,{center:{lat:56,lng:10},zoom:6,minZoom:MAP_MIN_ZOOM,maxZoom:WEB_MAP_MAX_ZOOM,mapTypeId:'roadmap',streetViewControl:false,fullscreenControl:false,clickableIcons:false,gestureHandling:'cooperative',keyboardShortcuts:true,mapTypeControl:false,cameraControl:false,zoomControl:false});
 const markers=new Map(),solid=[],estimated=[],lineCache=new WeakMap();
 let props={},circle=null,circleOutline=null,signature=null,presentation=null,destroyed=false,lastFollow=null;
 let lastZoomState=null;
 const publishZoom=()=>{
  if(destroyed)return;
  const state=zoomAvailability(map.getZoom(),MAP_MIN_ZOOM,WEB_MAP_MAX_ZOOM);
  if(!lastZoomState||state.in!==lastZoomState.in||state.out!==lastZoomState.out){lastZoomState=state;onZoomChange(state);}
 };
 const zoomChanged=map.addListener('zoom_changed',publishZoom),idle=map.addListener('idle',publishZoom);
 publishZoom();
 // A card width can change without a new fix. Keep its camera and map instance.
 const resize=typeof ResizeObserver==='function'?new ResizeObserver(()=>{if(!destroyed)maps.event.trigger?.(map,'resize');}):null;
 resize?.observe(element);
 const click=map.addListener('click',event=>{
  if(destroyed||!props.onPick||!event.latLng)return;
  const point={latitude:event.latLng.lat(),longitude:event.latLng.lng()};
  if(valid(point))props.onPick(point);
 });
 const fit=()=>{
  if(destroyed)return;
  const points=fleetPoints(props.devices??[]);
  const coordinates=(props.fitPoints??[...points.map(d=>d.position),...(props.lines??[]).flat(),...(props.estimatedLines??[]).flat()]).filter(valid);
  if(circle){const bounds=circle.getBounds();if(bounds)map.fitBounds(bounds,48);return;}
  if(!coordinates.length)return;
  if(coordinates.length===1){map.setCenter(coordinate(coordinates[0]));map.setZoom(15);return;}
  const bounds=new maps.LatLngBounds();for(const p of coordinates)bounds.extend(coordinate(p));map.fitBounds(bounds,48);
 };
 const syncLines=(overlays,lines,isEstimated,colors=[])=>{
  for(let i=0;i<lines.length;i++){
   const color=colors[i]??(isEstimated?'#A1DDCE':'#168b7a');
   if(!overlays[i])overlays[i]=new maps.Polyline({map,clickable:false,strokeColor:color,strokeWeight:isEstimated?5:4,strokeOpacity:1,zIndex:isEstimated?1:2});
   const cached=lineCache.get(overlays[i]);
   if(cached?.line!==lines[i])overlays[i].setPath(lines[i].filter(valid).map(coordinate));
   if(cached?.color!==color)overlays[i].setOptions({strokeColor:color});
   lineCache.set(overlays[i],{line:lines[i],color});
  }
  while(overlays.length>lines.length)overlays.pop().setMap(null);
 };
 const update=next=>{
  if(destroyed)return;
  props=next;
  const points=fleetPoints(next.devices??[]),ids=new Set(points.map(d=>d.id));
  for(const [id,entry] of markers)if(!ids.has(id)){entry.listener.remove();entry.marker.setMap(null);markers.delete(id);}
  for(const device of points){
   let entry=markers.get(device.id);
   if(!entry){
    const marker=new maps.Marker({map,optimized:false});
    const listener=marker.addListener('click',()=>{if(!destroyed)props.onSelect?.(device.id);});
    entry={marker,listener};markers.set(device.id,entry);
   }
   entry.marker.setOptions({position:coordinate(device.position),label:null,opacity:['gap','stale'].includes(device.markerState)?.55:1,zIndex:next.highlightedId===device.id?100:10,title:deviceName(device),icon:{path:maps.SymbolPath.CIRCLE,scale:next.highlightedId===device.id?14:10,fillColor:device.routeColor??(device.connected===false?'#8c9498':next.color),fillOpacity:1,strokeColor:'#ffffff',strokeWeight:3}});
  }
  syncLines(solid,next.lines??[],false,next.lineColors);syncLines(estimated,next.estimatedLines??[],true,next.estimatedLineColors);
  if(valid(next.zone)&&Number.isFinite(next.zone.radiusMeters)&&next.zone.radiusMeters>0){
   if(!circle){
    circleOutline=new maps.Circle({map,clickable:false,strokeColor:ZONE_STYLE.outline,strokeOpacity:1,strokeWeight:ZONE_STYLE.outlineWidth,fillColor:ZONE_STYLE.color,fillOpacity:ZONE_STYLE.opacity,zIndex:3});
    circle=new maps.Circle({map,clickable:false,strokeColor:ZONE_STYLE.color,strokeOpacity:1,strokeWeight:ZONE_STYLE.strokeWidth,fillOpacity:0,zIndex:4});
   }
   const geometry={center:coordinate(next.zone),radius:next.zone.radiusMeters};
   circleOutline.setOptions(geometry);circle.setOptions(geometry);
  }else if(circle){circle.setMap(null);circleOutline.setMap(null);circle=null;circleOutline=null;}
  const style=next.mapStyle==='satellite'?'satellite':'roadmap';
  const nextPresentation=JSON.stringify([next.mapStyle,Boolean(next.onPick)]);
  if(nextPresentation!==presentation){presentation=nextPresentation;map.setOptions({mapTypeId:style,styles:next.mapStyle==='current'?DARK_MAP_STYLE:[],draggableCursor:next.onPick?'crosshair':null});}
  const routeKey=next.fitPoints!==undefined?next.fitKey:JSON.stringify((next.lines??[]).map(line=>[line.length,line[0],line.at(-1)]));
  const nextSignature=JSON.stringify([(next.devices??[]).map(d=>d.id).sort(),next.fitPoints!==undefined?null:[...ids].sort(),next.fitPoints!==undefined,routeKey,next.zone??null]);
  if(nextSignature!==signature){
   signature=nextSignature;
   if(!circle&&valid(next.initialFocus)){map.setCenter(coordinate(next.initialFocus));map.setZoom(15);}
   else fit();
  }
  const follow=valid(next.followPoint)?JSON.stringify(coordinate(next.followPoint)):null;
  if(follow!==lastFollow){lastFollow=follow;if(follow&&!circle)map.setCenter(coordinate(next.followPoint));}
 };
 return {update,fit,zoom(direction){if(!destroyed){const current=map.getZoom(),target=nextZoom(current,direction,MAP_MIN_ZOOM,WEB_MAP_MAX_ZOOM);if(target!==null&&target!==current)map.setZoom(target);publishZoom();}},pickCenter(){const center=map.getCenter();if(center&&props.onPick)props.onPick({latitude:center.lat(),longitude:center.lng()});},destroy(){
  if(destroyed)return;destroyed=true;resize?.disconnect();click.remove();zoomChanged.remove();idle.remove();
  for(const {marker,listener} of markers.values()){listener.remove();marker.setMap(null);}markers.clear();
  for(const overlay of [...solid,...estimated])overlay.setMap(null);circle?.setMap(null);circleOutline?.setMap(null);
  maps.event.clearInstanceListeners(map);
 }};
}
